import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

import Modal from '../../../widgets/Modal';
import Button from '../../../widgets/Button';
import { DayPicker } from '../../../widgets/Daypicker/Daypicker';
import { Typography } from '../../../widgets/Typography';

import { IDateRange } from '../../../../shared/types/filters';
import { ISO_DATE_FORMAT } from '../../../../shared/constants/date';
import { formatDateToDisplayLabel } from '../../../../utilities/common/date';

interface DateRangePickerModalProps {
  /** Controls whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Current date range selection */
  dateRange: IDateRange;
  /** Callback when user applies the date range */
  onApply: (appliedDateRange: IDateRange) => void;
  /** Callback when date range changes internally (before apply) */
  onDateRangeChange?: (dateRange: IDateRange) => void;
  /** Maximum selectable date (inclusive) */
  maxDate?: string | Date;
  /** Optional title for the modal header */
  title?: string;
  /** Optional text for the apply button */
  applyButtonText?: string;
  /** Optional text for the cancel button */
  cancelButtonText?: string;
}

function DateRangePickerModal({
  open,
  onClose,
  dateRange,
  onDateRangeChange,
  onApply,
  maxDate,
  title,
  applyButtonText,
  cancelButtonText,
}: DateRangePickerModalProps) {
  const { t } = useTranslation();

  // Internal state to track date selection before Apply
  const [internalDateRange, setInternalDateRange] =
    useState<IDateRange>(dateRange);

  // Sync internal state when modal opens or dateRange prop changes
  useEffect(() => {
    if (open) {
      setInternalDateRange(dateRange);
    }
  }, [open, dateRange]);

  const fromDate = new Date(internalDateRange.fromDate);
  const toDate = new Date(internalDateRange.toDate);
  const maxDateObj = maxDate ? new Date(maxDate) : new Date();

  const handleFromDateSelect = (_day: Date | undefined, selected: Date) => {
    const updatedRange = {
      ...internalDateRange,
      fromDate: format(selected, ISO_DATE_FORMAT),
    };

    setInternalDateRange(updatedRange);
    onDateRangeChange?.(updatedRange);
  };

  const handleToDateSelect = (_day: Date | undefined, selected: Date) => {
    const updatedRange = {
      ...internalDateRange,
      toDate: format(selected, ISO_DATE_FORMAT),
    };

    setInternalDateRange(updatedRange);
    onDateRangeChange?.(updatedRange);
  };

  return (
    <Modal
      open={open}
      handleClose={onClose}
      modalClassName="!p-0 overflow-visible"
    >
      <div className="border-grey-100 border-b p-6">
        <Typography variant="body-xl" weight="bold" className="text-grey-900">
          {title || 'Select date'}
        </Typography>
      </div>

      <div className={`divide-grey-100 divide-x md:flex`}>
        {/* From Date Picker */}
        <div className="px-4">
          <div className="py-4">
            <Typography
              variant="body-sm"
              weight="medium"
              className="text-grey-800 mb-1.5"
            >
              From
            </Typography>
            <DayPicker
              defaultMonth={fromDate}
              mode="single"
              selected={fromDate}
              onSelect={handleFromDateSelect}
              disabled={[{ after: maxDateObj }, { after: toDate }]}
              endMonth={
                new Date(new Date().getFullYear(), new Date().getMonth())
              }
            />
          </div>
        </div>

        {/* To Date Picker */}
        <div className="px-4">
          <div className="py-4">
            <Typography
              variant="body-sm"
              weight="medium"
              className="text-grey-800 mb-1.5"
            >
              To
            </Typography>
            <DayPicker
              defaultMonth={toDate}
              mode="single"
              selected={toDate}
              onSelect={handleToDateSelect}
              disabled={[{ after: maxDateObj }, { before: fromDate }]}
              endMonth={new Date(new Date().getFullYear(), 11)}
            />
          </div>
        </div>
      </div>

      {/* Footer with date display and action buttons */}
      <div
        className={`border-black-200 flex flex-col items-center justify-between gap-y-4 border-t p-4 px-6 md:flex-row md:items-end`}
      >
        <div className={`flex w-full items-center gap-x-3 md:w-auto`}>
          <div className="w-full md:w-auto">
            <div className="border-black-300 text-black-400 cursor-default rounded-xl border p-3 text-sm shadow-[0px_1px_2px_0px_#1018280D]">
              {formatDateToDisplayLabel(fromDate)}
            </div>
          </div>
          <div className="border-grey-500 h-0.5 w-3 border bg-gray-500" />
          <div className="w-full md:w-auto">
            <div className="border-grey-100 text-black-400 cursor-default rounded-xl border p-3 text-sm shadow-[0px_1px_2px_0px_#1018280D]">
              {formatDateToDisplayLabel(toDate)}
            </div>
          </div>
        </div>

        <div className="gap flex w-full flex-row gap-3 md:w-auto">
          <Button
            variant="outline"
            className="w-full px-12 md:w-auto"
            onClick={onClose}
          >
            {cancelButtonText || t('filterCancel')}
          </Button>
          <Button
            className="w-full px-12 md:w-auto"
            onClick={() => onApply(internalDateRange)}
          >
            {applyButtonText || t('APPLY')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DateRangePickerModal;
